import { useState, useRef, useEffect } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Build a WebP proxy URL for a given image source.
 * Falls back to the original if the source isn't an http URL.
 */
const webpUrl = (src, width) => {
  if (!src || !src.startsWith('http')) return null;
  const params = new URLSearchParams({ url: src, q: '75' });
  if (width) params.set('w', String(width));
  return `${API}/img/webp?${params}`;
};

/**
 * Build a responsive srcSet string with 1×, 1.5× and 2× widths.
 * The browser picks the best match based on the `sizes` attribute.
 */
const buildSrcSet = (src, baseWidth) => {
  if (!src?.startsWith('http') || !baseWidth) return null;
  const widths = [baseWidth, Math.round(baseWidth * 1.5), baseWidth * 2].filter(w => w <= 2400);
  return widths.map(w => `${webpUrl(src, w)} ${w}w`).join(', ');
};

/**
 * Skeleton placeholder — subtle shimmer animation
 */
const ImageSkeleton = ({ className }) => (
  <div
    className={`bg-[#EDECE4] animate-pulse ${className || ''}`}
    style={{ position: 'absolute', inset: 0 }}
    data-testid="image-skeleton"
  />
);

/**
 * Error fallback — subtle brand-colored placeholder
 */
const ImageFallback = ({ alt, className }) => (
  <div
    className={`bg-[#F0EFE7] flex items-center justify-center ${className || ''}`}
    style={{ position: 'absolute', inset: 0 }}
    data-testid="image-fallback"
  >
    <div className="text-center px-4">
      <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-[#E5D8D0]/40 flex items-center justify-center">
        <svg className="w-5 h-5 text-[#C4B5A8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
        </svg>
      </div>
      <p className="text-xs text-[#B0A89E] tracking-wide">{alt || 'Image'}</p>
    </div>
  </div>
);

/**
 * OptimizedImage — renders a <picture> with:
 * - WebP source via proxy + original fallback
 * - Skeleton shimmer while loading
 * - Graceful error fallback
 * - Smooth fade-in on load
 * - decoding="async" + loading="lazy" by default
 */
export const OptimizedImage = ({
  src, alt, className, width, loading = 'lazy',
  fetchPriority, sizes, showSkeleton = true, ...rest
}) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const imgRef = useRef(null);

  // If the image is already cached by the browser, complete fires before useEffect
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const wp = webpUrl(src, width);
  const srcSet = buildSrcSet(src, width);

  // If no src provided or empty string, show fallback immediately
  if (!src || errored) {
    return (
      <div className={`relative ${className || ''}`} style={{ position: 'relative' }}>
        <ImageFallback alt={alt} />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {showSkeleton && !loaded && <ImageSkeleton className={className} />}
      <picture>
        {srcSet && <source srcSet={srcSet} type="image/webp" sizes={sizes} />}
        {wp && !srcSet && <source srcSet={wp} type="image/webp" sizes={sizes} />}
        <img
          ref={imgRef}
          src={src}
          alt={alt || ''}
          className={className}
          loading={loading}
          decoding="async"
          fetchPriority={fetchPriority}
          sizes={sizes}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          style={{
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
          {...rest}
        />
      </picture>
    </div>
  );
};
